import { useAuth } from 'context/AuthContext'
import useQuery from 'hooks/useQuery'
import React, { useContext, useEffect, useState } from 'react'
import { Button, Dialog } from '@mui/material'
import VideoSDK from 'components/VideoSKD'
import { getDayTimeFromMinutes } from 'helpers/dayTime'
import { Roles } from 'utils/enums'
import useIsMobile from 'utils/useIsMobile'
import { toastContext } from 'context/ToastContext'
import usePersistedCountdown from 'hooks/usePersistedCountdown'
import { AccountDataContext } from 'context/AccountContext'
import { buildAccountSessionQuery } from 'helpers/accountSession'
import { useSkipWaitingRoom } from './hooks'
import ExplanatoryVideo from './ExplanatoryVideo'

const aidTelephone = '+54 9 11 4838-0679'

const AccountDashboard = () => {
  const { user } = useAuth()
  const { setToastOptions } = useContext(toastContext)
  const {
    availableRoom,
    setAvailableRoom,
    socketConnected,
    setCurrentRequestId,
    browserSessionId,
    clientSessionId,
  } = useContext(AccountDataContext)

  const { makeRequest: requestSupport } = useQuery({
    defaultEndpoint: '/accounts/request',
    isControlled: true,
    toastOptions: {
      duration: 10,
      resultMessageError: {
        503: 'No hay intérpretes disponibles, intente más tarde',
      },
    },
  })
  const { makeRequest: cancelRequest } = useQuery({
    defaultEndpoint: '/accounts/cancelRequest',
    isControlled: true,
  })

  const [roomId, setRoomId] = useState(null)
  const [isCancellingReadyCall, setIsCancellingReadyCall] = useState(false)

  const [
    requestButtonDisabledRemainingTime,
    setRequestButtonDisabledRemainingTime,
  ] = usePersistedCountdown({ key: `enableRequestRemainingTime:${browserSessionId}` })
  const [disabled, setDisabled] = useState(true)

  const [waitingModalRemainingTime, setWaitingModalRemainingTime] = usePersistedCountdown({
    key: `pendingRequest:${browserSessionId}`,
    onFinish: async ({ manualCancelation }) => {
      const toastOptions = manualCancelation
        ? {
          showFeedbackMessage: true,
          resultMessageSuccess: 'Se ha cancelado la petición',
        }
        : undefined
      const { error } = await cancelRequest({
        endpoint: `/accounts/cancelRequest?${buildAccountSessionQuery({ browserSessionId, clientSessionId })}`,
        toastOptions,
        hideGlobalLoading: true,
      })
      if (!error) setCurrentRequestId(null)
      if (manualCancelation) {
        const secondsToWaitAfterRequestCancelation = 30
        setRequestButtonDisabledRemainingTime(
          secondsToWaitAfterRequestCancelation,
        )
      } else {
        setToastOptions({
          message: 'Su llamada no ha sido respondida',
          error: true,
          duration: 10000,
        })
      }
    },
  })
  const requestSupportHandler = async () => {
    const { error, data } = await requestSupport({
      endpoint: `/accounts/request?${buildAccountSessionQuery({ browserSessionId, clientSessionId })}`,
    })
    if (!error) {
      setCurrentRequestId(data?.requestId)
      if (!data?.reused || !waitingModalRemainingTime) {
        setWaitingModalRemainingTime(180)
      }
    }
  }

  useEffect(() => {
    if (availableRoom) setWaitingModalRemainingTime(null)
    else setRoomId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRoom])

  useEffect(() => {
    setTimeout(() => setDisabled(false), 10_000)
  }, [])

  useSkipWaitingRoom(requestSupportHandler)

  const cancelReadyRoomHandler = async () => {
    setIsCancellingReadyCall(true)
    const { error } = await cancelRequest({
      endpoint: `/accounts/cancelRequest?${buildAccountSessionQuery({
        browserSessionId,
        clientSessionId,
        callId: availableRoom.callId,
      })}`,
      toastOptions: {
        showFeedbackMessage: true,
        resultMessageSuccess: 'Se canceló la llamada',
      },
      hideGlobalLoading: true,
    })
    setIsCancellingReadyCall(false)
    if (!error) {
      setAvailableRoom(null)
      setCurrentRequestId(null)
      setWaitingModalRemainingTime(null)
      setRoomId(null)
      const secondsToWaitAfterRequestCancelation = 30
      setRequestButtonDisabledRemainingTime(secondsToWaitAfterRequestCancelation)
    }
  }

  if (roomId) {
    return (
      <div className='w-full h-[100vh] relative z-20 mt-[-9vh]'>
        <VideoSDK
          meetingId={roomId}
          token={availableRoom?.token}
          participantName={`${user.firstName} ${user.lastName}`}
          roleObject={Roles.Account}
          remountOnPageResume
        />
      </div>
    )
  }

  return (
    <div className='flex justify-center mt-[5vh]'>
      {availableRoom?.roomId ? (
        <div className='flex flex-col items-center gap-3'>
          <Button
            onClick={() => setRoomId(availableRoom.roomId)}
            disabled={disabled}
          >
            {disabled ? 'Cargando...' : 'Iniciar Videollamada'}
          </Button>
          <Button
            variant='outlined'
            color='secondary'
            onClick={cancelReadyRoomHandler}
            disabled={isCancellingReadyCall}
          >
            {isCancellingReadyCall ? 'Cancelando…' : 'Cancelar llamada'}
          </Button>
        </div>
      ) : !waitingModalRemainingTime ? (
        <div className='flex flex-col justify-center items-center h-[30vh]'>
          <Button
            color='primary'
            onClick={requestSupportHandler}
            sx={{ fontSize: '1.25em' }}
            disabled={!!requestButtonDisabledRemainingTime || !socketConnected}
            className='max-w-[80vw]'
          >
            {requestButtonDisabledRemainingTime
              ? `Espere ${requestButtonDisabledRemainingTime} segundos para volver a solicitar ayuda...`
              : 'Solicitar Sala'}
          </Button>
        </div>
      ) : null}
      {!!waitingModalRemainingTime && (
        <WaitingModal
          waitingModalRemainingTime={waitingModalRemainingTime}
          setWaitingModalRemainingTime={setWaitingModalRemainingTime}
          country={user.country}
        />
      )}
      <p className='fixed bottom-0 right-0 m-4'>
        Si su llamada no ha sido respondida, comuníquese al
        {' '}
        <a
          className='underline'
          href={`tel:${aidTelephone.replaceAll(/[^\d]/g, '')}`}
        >
          {aidTelephone}
        </a>
      </p>
    </div>
  )
}

const WaitingModal = ({
  waitingModalRemainingTime,
  setWaitingModalRemainingTime,
  country,
}) => {
  const requestCancelationHandler = async () => {
    setWaitingModalRemainingTime(0, { manualCancelation: true })
  }

  const isMobile = useIsMobile()

  return (
    <Dialog open fullScreen={isMobile}>
      <div className={`flex flex-col items-center align-items-center justify-center w-full h-full shadow-modalInset ${isMobile ? '' : 'gap-2 p-[max(3vw,_3vh)]'}`}>
        <h2>Creando la reunión</h2>
        <h3>
          Por favor espere
          {' '}
          <span className='underline'>
            {getDayTimeFromMinutes(waitingModalRemainingTime)}
          </span>
          {' '}
          minutos
        </h3>
        {isMobile && <ExplanatoryVideo country={country} />}
        <Button onClick={requestCancelationHandler}>Cancelar petición</Button>
      </div>
    </Dialog>
  )
}

export default AccountDashboard
